import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, ArrowRight, User, MessageSquare, CheckCircle2 } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { api, formatApiError } from "../lib/api";
import { Field } from "./Register";

export default function Contact() {
  const { t, lang } = useI18n();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/contact", { name, email, message, language: lang });
      setSent(true);
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-16 md:py-24 max-w-4xl">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="font-mono-label text-gold/80 mb-4">{t.contact.kicker}</p>
            <h1 className="font-display text-section md:text-section-lg tracking-tight leading-[1.05]">
              {t.contact.title}
            </h1>
            <p className="mt-6 text-cream-muted text-body">{t.contact.sub}</p>

            <div className="mt-12">
              <p className="font-mono-label text-cream-dim mb-3">{t.contact.direct}</p>
              <a href="mailto:hello@thelifeplot.com" className="text-card text-gold hover:text-gold-hover">
                hello@thelifeplot.com
              </a>
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="glass rounded-3xl p-8 md:p-10"
          >
            {sent ? (
              <>
                <CheckCircle2 className="h-9 w-9 text-gold" />
                <h2 className="mt-6 font-display text-card-lg">{t.contact.sent}</h2>
                <p className="mt-3 text-cream-muted">
                  {lang === "en"
                    ? "We typically reply within one business day."
                    : "În general răspundem în maximum o zi lucrătoare."}
                </p>
              </>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5">
                <Field icon={<User className="h-4 w-4 text-cream-dim" />} label={t.contact.name}>
                  <input
                    data-testid="contact-name"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t.contact.name}
                    className="input-bare"
                  />
                </Field>
                <Field icon={<Mail className="h-4 w-4 text-cream-dim" />} label={t.contact.email}>
                  <input
                    data-testid="contact-email"
                    required
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.contact.email}
                    className="input-bare"
                  />
                </Field>
                <div>
                  <label className="block font-mono-label text-cream-dim mb-2">{t.contact.message}</label>
                  <textarea
                    data-testid="contact-message"
                    required
                    minLength={4}
                    rows={5}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={t.contact.message}
                    className="input-field resize-none"
                  />
                </div>
                {error && <p className="text-caption text-red-300/90">{error}</p>}
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={loading}
                  className="btn btn-gold btn-lg w-full"
                >
                  {loading ? t.common.sending : t.contact.send}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
